/* Dashboard Builder — host wrapper with named-board switcher.
 *
 * Shows a board picker (quick-switch dropdown) + New/Edit controls on the
 * Dashboard & Analytics pages. Resolves which board to show on load:
 * user default → tenant default → built-in default page. Anyone can create
 * personal boards; only admins can create/edit shared boards. On a custom
 * Analytics board a filter bar narrows all tiles together (cleared each visit). */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { LayoutDashboard, Plus, Pencil, Star } from 'lucide-react';
import { DashPage, DashFilters, Preset } from './types';
import { usePresets, setDefaultPreset } from './useLayouts';
import { LayoutRenderer } from './LayoutRenderer';
import { BuilderOverlay } from './BuilderOverlay';
import { AnalyticsFilterBar } from './FilterBar';

export const CustomiseHost: React.FC<{ page: DashPage; defaultContent: React.ReactNode }>
  = ({ page, defaultContent }) => {
  const { resp, reload } = usePresets(page);
  // active board id: undefined = not yet resolved, '' = built-in default page.
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<false | { preset: Preset | null }>(false);
  const [filters, setFilters] = useState<DashFilters>({});

  useEffect(() => {
    if (!resp || activeId !== undefined) return;
    setActiveId(resp.user_default_preset_id || resp.tenant_default_preset_id || '');
  }, [resp, activeId]);

  if (!resp) return <>{defaultContent}</>;

  const presets = resp.presets;
  const shared = presets.filter((p) => p.scope === 'shared');
  const mine = presets.filter((p) => p.scope === 'personal');
  const active = activeId ? presets.find((p) => p.id === activeId) || null : null;
  const userDefault = resp.user_default_preset_id || '';
  const currentSel = activeId ?? '';

  const makeDefault = async (): Promise<void> => {
    try {
      await setDefaultPreset(page, activeId || null);
      toast.success(activeId ? 'Set as your default board.' : 'Reverted to the built-in default.');
      await reload();
    } catch { toast.error('Could not set default.'); }
  };

  return (
    <div className="space-y-4">
      {/* Board switcher toolbar */}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <LayoutDashboard size={15} className="text-raptor-navy/50" />
        <Select
          value={currentSel}
          onChange={(e) => setActiveId(e.target.value)}
          className="w-60 h-9"
          data-testid={`board-switcher-${page}`}
        >
          <option value="">Default page (built-in)</option>
          {shared.length > 0 && (
            <optgroup label="Shared boards">
              {shared.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          )}
          {mine.length > 0 && (
            <optgroup label="My boards">
              {mine.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          )}
        </Select>
        {currentSel !== userDefault && (
          <Button size="sm" variant="ghost" onClick={() => void makeDefault()} data-testid={`board-set-default-${page}`}>
            <Star size={14} /> Set as default
          </Button>
        )}
        {active && active.can_edit && (
          <Button size="sm" variant="outline" onClick={() => setEditing({ preset: active })} data-testid={`board-edit-${page}`}>
            <Pencil size={14} /> Edit
          </Button>
        )}
        <Button size="sm" variant="default" onClick={() => setEditing({ preset: null })} data-testid={`board-new-${page}`}>
          <Plus size={14} /> New board
        </Button>
      </div>

      {/* Content */}
      {active ? (
        <div className="space-y-4">
          {page === 'analytics' && <AnalyticsFilterBar filters={filters} onChange={setFilters} />}
          <LayoutRenderer layout={{ tiles: active.tiles, colors: active.colors }} filters={page === 'analytics' ? filters : undefined} />
        </div>
      ) : defaultContent}

      {editing && (
        <BuilderOverlay
          page={page}
          preset={editing.preset}
          canEditShared={resp.can_edit_shared}
          onClose={() => setEditing(false)}
          onSaved={(savedId) => { setActiveId(savedId ?? ''); void reload(); }}
        />
      )}
    </div>
  );
};
